/**
 * Pure reducer from RelayEvent to the phone's view of the world. Signals wrap it (state.ts);
 * every event is zod-parsed by the client before it gets here.
 */
import type { CallSummary, NodeState, RelayEvent } from '@ccphone/contracts';
import type { z } from 'zod';
import type { RosterEntry as RosterEntrySchema } from '@ccphone/contracts';
type RosterEntry = z.infer<typeof RosterEntrySchema>;

export interface HeldBridge {
  call_id: string;
  bridge_id: string;
  roster: RosterEntry[];
  expires_at: string;
}

export interface MissedCall {
  call_id: string;
  remote_number?: string;
  remote_name?: string;
  at: string;
}

export interface RecentCall {
  call_id: string;
  direction: CallSummary['direction'];
  remote_number?: string;
  remote_name?: string;
  outcome: string;
  started_at: string;
  ended_at: string;
}

export interface Revocation {
  reason: 'logout' | 'admin' | 'zombie' | 'expired' | 'superseded';
  message?: string;
  at: string;
}

export interface PhoneState {
  /** live calls keyed by call_id; ended calls are removed */
  calls: Record<string, CallSummary>;
  heldBridges: HeldBridge[];
  presence?: string;
  pausedQueues: string[];
  nodeState: NodeState;
  nodeEtaSec?: number;
  nodeIncidentId?: string;
  nodeMessage?: string;
  /** server time when the node_state was last reported (for the ETA countdown) */
  nodeStateAt?: string;
  maintenance: { active: boolean; message?: string; restart_at?: string };
  control: 'ok' | 'reconnecting' | 'down';
  mwi: { waiting: boolean; new: number; old: number };
  revoked: Revocation | null;
  missed: MissedCall[];
  recent: RecentCall[];
  serverTime?: string;
  /** set once a `state` event has been applied (full snapshot received) */
  synced: boolean;
}

export const MAX_MISSED = 50;
export const MAX_RECENT = 20;

export function initialPhoneState(): PhoneState {
  return {
    calls: {},
    heldBridges: [],
    pausedQueues: [],
    nodeState: 'up',
    maintenance: { active: false },
    control: 'ok',
    mwi: { waiting: false, new: 0, old: 0 },
    revoked: null,
    missed: [],
    recent: [],
    synced: false,
  };
}

/**
 * The consult of a call as the roster shows it. `call.roster` is the only event the orchestrator sends when a
 * consult (transfer.attended.start, or conf.add on a 1:1 call) starts, moves or ends, so the consult block (Merge,
 * Swap, Complete transfer) follows the roster's live `consult` entry; the summary's target is kept when known.
 */
function consultFromRoster(roster: readonly RosterEntry[], prev: CallSummary['consult']): CallSummary['consult'] {
  const entry = roster.find((r) => r.kind === 'consult' && r.state !== 'ended');
  if (!entry) return undefined;
  const target = prev && prev.channel_id === entry.channel_id ? prev.target : (entry.number ?? entry.label);
  return { channel_id: entry.channel_id, target, state: entry.state };
}

function withCall(s: PhoneState, id: string, patch: (c: CallSummary) => CallSummary): PhoneState {
  const c = s.calls[id];
  if (!c) return s;
  return { ...s, calls: { ...s.calls, [id]: patch(c) } };
}

function pushRecent(s: PhoneState, r: RecentCall): RecentCall[] {
  return [r, ...s.recent.filter((x) => x.call_id !== r.call_id)].slice(0, MAX_RECENT);
}

export function reduce(s: PhoneState, ev: RelayEvent): PhoneState {
  switch (ev.t) {
    case 'ack':
      return s;
    case 'state': {
      const calls: Record<string, CallSummary> = {};
      for (const c of ev.calls) if (c.state !== 'ended') calls[c.call_id] = c;
      return {
        ...s,
        calls,
        heldBridges: ev.held_bridges,
        presence: ev.presence ?? s.presence,
        pausedQueues: ev.paused_queues,
        nodeState: ev.node_state,
        nodeStateAt: ev.server_time,
        maintenance: { ...s.maintenance, active: ev.maintenance },
        control: ev.control,
        serverTime: ev.server_time,
        synced: true,
      };
    }
    case 'call.ringing':
    case 'call.dialing':
      return { ...s, calls: { ...s.calls, [ev.call.call_id]: ev.call } };
    case 'call.answered':
      return withCall(s, ev.call_id, (c) => ({ ...c, state: 'answered', answered_at: ev.answered_at, held_by: undefined }));
    case 'call.held':
      return withCall(s, ev.call_id, (c) => ({ ...c, state: 'held', held_by: ev.by }));
    case 'call.resumed':
      return withCall(s, ev.call_id, (c) => ({ ...c, state: 'answered', held_by: undefined }));
    case 'call.roster':
      return withCall(s, ev.call_id, (c) => ({ ...c, roster: ev.roster, conference: ev.conference, consult: consultFromRoster(ev.roster, c.consult) }));
    case 'call.ended': {
      const c = s.calls[ev.call_id];
      const { [ev.call_id]: _gone, ...rest } = s.calls;
      const recent = c
        ? pushRecent(s, { call_id: ev.call_id, direction: c.direction, remote_number: c.remote_number, remote_name: c.remote_name, outcome: ev.outcome, started_at: c.started_at, ended_at: ev.ended_at })
        : s.recent;
      return { ...s, calls: rest, heldBridges: s.heldBridges.filter((h) => h.call_id !== ev.call_id), recent };
    }
    case 'call.missed': {
      const { [ev.call_id]: _gone, ...rest } = s.calls;
      const entry: MissedCall = { call_id: ev.call_id, remote_number: ev.remote_number, remote_name: ev.remote_name, at: ev.at };
      return { ...s, calls: rest, missed: [entry, ...s.missed.filter((m) => m.call_id !== ev.call_id)].slice(0, MAX_MISSED) };
    }
    case 'call.rejoinable': {
      const hb: HeldBridge = { call_id: ev.call_id, bridge_id: ev.bridge_id, roster: ev.roster, expires_at: ev.expires_at };
      return { ...s, heldBridges: [hb, ...s.heldBridges.filter((h) => h.call_id !== ev.call_id)] };
    }
    case 'presence':
      return { ...s, presence: ev.status, pausedQueues: ev.paused_queues };
    case 'control':
      return { ...s, control: ev.ari };
    case 'node_state': {
      const next: PhoneState = { ...s, nodeState: ev.state, nodeEtaSec: ev.eta_sec, nodeIncidentId: ev.incident_id, nodeMessage: ev.message, nodeStateAt: ev.at };
      // a node restart destroys every bridge: nothing can be rejoined
      if (ev.state === 'restarting' || ev.state === 'down') next.heldBridges = [];
      return next;
    }
    case 'maintenance':
      return { ...s, maintenance: { active: ev.active, message: ev.message, restart_at: ev.restart_at } };
    case 'session.revoked':
      return { ...s, revoked: { reason: ev.reason, message: ev.message, at: ev.at } };
    case 'mwi':
      return { ...s, mwi: { waiting: ev.waiting, new: ev.new, old: ev.old } };
    default:
      return s;
  }
}

export function liveCalls(s: PhoneState): CallSummary[] {
  return Object.values(s.calls).sort((a, b) => a.started_at.localeCompare(b.started_at));
}

/** calls that need a browser leg but have none attached yet (used to match INVITEs) */
export function callsAwaitingLeg(s: PhoneState, attached: ReadonlySet<string>): CallSummary[] {
  return liveCalls(s).filter((c) => (c.state === 'ringing' || c.state === 'dialing') && !attached.has(c.call_id));
}
